import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { GetInterventionLadderResponse, InterventionLadderPeriod } from '../../../api-service/clients';
import { formatNumber } from '../../../utilities';
import { formatMillions } from './intervention-cost';
import { bandColor } from './intervention-ladder-colors';

const withoutPriceColor = '#f5b14c';

export interface InterventionLadderCaptionProps {
  period: InterventionLadderPeriod;
  currentYear: number;
  ladder: GetInterventionLadderResponse | undefined;
  onSelectPeriod: (period: InterventionLadderPeriod) => void;
}

export function formatPeriod(period: InterventionLadderPeriod, currentYear: number) {
  switch (period) {
    case InterventionLadderPeriod.ThisYear:
      return `${currentYear}`;
    case InterventionLadderPeriod.LastYear:
      return `${currentYear - 1}`;
    case InterventionLadderPeriod.YearBefore:
      return `${currentYear - 2}`;
    default:
      return 'Sidste 12 mdr.';
  }
}

export function InterventionLadderCaption({ period, currentYear, ladder, onSelectPeriod }: InterventionLadderCaptionProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3, flexWrap: 'wrap', px: 2.5, py: 2.5, bgcolor: bandColor, color: '#ffffff' }}>
      <Box>
        <Typography variant="h2" sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
          Indsatstrappen
        </Typography>
        <Typography component="div" sx={{ fontSize: '0.6875rem', fontWeight: 400, mb: 1.5 }}>
          Flyt borgerne <strong>ned ad trappen</strong> – mod størst mulig selvstændighed
        </Typography>
        <PeriodToggle period={period} currentYear={currentYear} onSelectPeriod={onSelectPeriod} />
      </Box>
      {ladder && (
        <Box sx={{ display: 'flex', alignSelf: 'center' }}>
          <KeyFigure label="Borgere" value={formatNumber(ladder.total.citizens)} />
          <KeyFigure label="Årspersoner" value={formatNumber(ladder.total.personYears, 1)} />
          <KeyFigure
            label="Udgift"
            value={formatMillions(ladder.total.cost.total)}
            unit="mio. kr."
            detail={`Intern ${formatMillions(ladder.total.cost.internal)} · Ekstern ${formatMillions(ladder.total.cost.external)}`}
          />
          {ladder.total.cost.activitiesWithoutPrice > 0 && (
            <KeyFigure label="Uden pris" value={formatNumber(ladder.total.cost.activitiesWithoutPrice)} valueColor={withoutPriceColor} detail="aktiviteter" />
          )}
        </Box>
      )}
    </Box>
  );
}

interface KeyFigureProps {
  label: string;
  value: string;
  unit?: string;
  detail?: string;
  valueColor?: string;
}

function KeyFigure({ label, value, unit, detail, valueColor }: KeyFigureProps) {
  return (
    <Box sx={{ px: 2.5, borderLeft: '1px solid rgba(255, 255, 255, 0.2)', minWidth: 110 }}>
      <Box sx={{ fontSize: '0.75rem', opacity: 0.85 }}>{label}</Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Box sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.25, fontVariantNumeric: 'tabular-nums', color: valueColor }}>{value}</Box>
        {unit && <Box sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{unit}</Box>}
      </Box>
      {detail && <Box sx={{ fontSize: '0.6875rem', opacity: 0.85 }}>{detail}</Box>}
    </Box>
  );
}

interface PeriodToggleProps {
  period: InterventionLadderPeriod;
  currentYear: number;
  onSelectPeriod: (period: InterventionLadderPeriod) => void;
}

function PeriodToggle({ period, currentYear, onSelectPeriod }: PeriodToggleProps) {
  const periods = [InterventionLadderPeriod.Last12Months, InterventionLadderPeriod.ThisYear, InterventionLadderPeriod.LastYear, InterventionLadderPeriod.YearBefore].map(
    option => ({ period: option, label: formatPeriod(option, currentYear) })
  );

  return (
    <ToggleButtonGroup exclusive size="small" value={period} aria-label="Periode" onChange={(_, value: InterventionLadderPeriod | null) => value && onSelectPeriod(value)}>
      {periods.map(option => (
        <ToggleButton
          key={option.period}
          value={option.period}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.25,
            color: '#ffffff',
            borderColor: 'rgba(255, 255, 255, 0.35)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
            '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#ffffff', color: bandColor },
          }}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
